import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { tween } from '../motion/presets';
import { useReducedMotion } from '../motion/useReducedMotion';
import { cx } from '../internal/cx';

export type DisclosureProps = {
  /** Contenu du bouton d'ouverture (ex. « Voir toute la galerie »). */
  label: ReactNode;
  /** État contrôlé — fournir aussi `onOpenChange`. */
  open?: boolean;
  /** État initial en mode non contrôlé. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * true (défaut) : le contenu replié reste monté, masqué et inerte — un repli
   * accidentel ne perd jamais une saisie de formulaire (spec 08 §2).
   * false : montage/démontage animé, pour du contenu sans état propre.
   */
  keepMounted?: boolean;
  children: ReactNode;
  className?: string;
  /** Classes additionnelles du bouton (typographie selon contexte). */
  triggerClassName?: string;
};

/**
 * Repli/dépli accessible : bouton `aria-expanded`/`aria-controls`, chevron
 * rotatif, animation hauteur 0 → auto (instantanée en reduced-motion).
 */
export function Disclosure({
  label,
  open,
  defaultOpen = false,
  onOpenChange,
  keepMounted = true,
  children,
  className,
  triggerClassName,
}: DisclosureProps) {
  const uid = useId();
  const reduced = useReducedMotion();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;
  const panelId = `${uid}-panel`;
  const panelRef = useRef<HTMLDivElement>(null);

  // `inert` DOIT être posé en effet avec une ref stable : framer-motion
  // mémoïse la ref des motion.* — un callback inline n'est invoqué qu'au
  // montage et `inert` resterait figé à true (champs dépliés incliquables).
  useEffect(() => {
    if (panelRef.current) panelRef.current.inert = !isOpen;
  }, [isOpen, keepMounted]);

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const transition = reduced ? { duration: 0 } : tween.base;

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
        className={cx(
          'flex min-h-[2.75rem] w-full items-center gap-2 rounded-lg text-left',
          'font-display text-sm font-semibold text-ink-100',
          'transition-colors duration-fast ease-fun hover:text-panda-400',
          triggerClassName,
        )}
      >
        <motion.span
          aria-hidden="true"
          initial={false}
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={reduced ? { duration: 0 } : tween.fast}
          className="inline-block text-ink-400"
        >
          ▸
        </motion.span>
        {label}
      </button>

      {keepMounted ? (
        <motion.div
          id={panelId}
          // Replié : toujours monté (l'état des champs survit) mais sorti de
          // l'arbre a11y (`aria-hidden`, synchrone) et du focus/interaction
          // (`inert`, posé en effet) — l'animation ne fait que l'habillage
          // visuel (hauteur + opacité, overflow-hidden).
          aria-hidden={!isOpen}
          ref={panelRef}
          initial={false}
          animate={isOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          transition={transition}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      ) : (
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              id={panelId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={transition}
              className="overflow-hidden"
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
